import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import api, { formatTRY } from "@/lib/api";
import HotelLayout from "@/components/HotelLayout";
import { PlusCircle, Phone, User, Save, LoaderCircle } from "lucide-react";
import { toast } from "sonner";
import { todayYMD, plusDaysYMD, toLocalYMD } from "@/lib/dateUtils";

const INPUT = "w-full px-3 py-2 rounded-xl bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 text-sm font-medium focus:ring-2 focus:ring-emerald-500/40 focus:border-emerald-500";

export default function HotelManualBookingPage() {
    const { user } = useAuth() || {};
    const navigate = useNavigate();
    const hotelId = user?.hotel_id;
    const [hotel, setHotel] = useState(null);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);

    const [form, setForm] = useState({
        check_in: todayYMD(),
        check_out: plusDaysYMD(1),
        room_type_id: "",
        rooms: 1,
        adults: 2,
        children: 0,
        child_ages: [],
        guest_first_name: "",
        guest_last_name: "",
        guest_email: "",
        guest_phone: "",
        special_requests: "",
        total_amount: 0,
        status: "confirmed",
        payment_status: "paid",
        source: "walk_in",
    });

    useEffect(() => {
        if (!hotelId) return;
        api.get(`/admin/hotels/${hotelId}`).then((r) => {
            setHotel(r.data);
            const first = (r.data.room_types || [])[0];
            if (first) setForm((f) => ({ ...f, room_type_id: first.id }));
        }).finally(() => setLoading(false));
    }, [hotelId]);

    const upd = (k, v) => {
        setForm((f) => {
            const next = { ...f, [k]: v };
            // Auto-fix checkout if before checkin
            if (k === "check_in" && next.check_out <= v) {
                const d = new Date(v);
                d.setDate(d.getDate() + 1);
                next.check_out = toLocalYMD(d);
            }
            return next;
        });
    };

    const nights = (() => {
        const a = new Date(form.check_in);
        const b = new Date(form.check_out);
        return Math.max(1, Math.round((b - a) / 86400000));
    })();

    const submit = async () => {
        if (!form.guest_first_name || !form.guest_last_name || !form.guest_phone) {
            toast.error("Misafir Ad/Soyad/Telefon zorunludur");
            return;
        }
        if (!form.room_type_id) { toast.error("Oda tipi seçin"); return; }
        if (form.total_amount <= 0) { toast.error("Toplam tutar gerekli"); return; }

        setSaving(true);
        try {
            const payload = { ...form, hotel_id: hotelId };
            await api.post("/admin/bookings", payload);
            toast.success("Rezervasyon eklendi");
            navigate("/h/today");
        } catch (e) {
            toast.error(e.response?.data?.detail || "Kayıt başarısız");
        } finally { setSaving(false); }
    };

    if (loading || !hotel) {
        return <HotelLayout title="Hızlı Rezervasyon"><div className="flex items-center justify-center py-20"><LoaderCircle className="h-10 w-10 animate-spin text-emerald-500" /></div></HotelLayout>;
    }

    return (
        <HotelLayout title="Hızlı Rezervasyon Ekle (Walk-in / Telefon)" action={
            <button data-testid="manual-save" onClick={submit} disabled={saving} className="px-4 py-2 rounded-xl bg-emerald-500 hover:bg-emerald-600 disabled:opacity-50 text-white text-sm font-bold inline-flex items-center gap-1.5"><Save className="h-4 w-4" /> {saving ? "Kaydediliyor..." : "Rezervasyon Oluştur"}</button>
        }>
            <div className="max-w-3xl space-y-5">
                {/* Dates & room */}
                <section className="p-5 rounded-2xl bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800">
                    <h2 className="font-bold text-slate-900 dark:text-white mb-3">Tarih & Oda</h2>
                    <div className="grid grid-cols-2 gap-3">
                        <label className="block"><span className="text-xs font-bold uppercase tracking-widest text-slate-500 mb-1 block">Giriş</span><input data-testid="mb-checkin" type="date" min={todayYMD()} value={form.check_in} onChange={(e) => upd("check_in", e.target.value)} className={INPUT} /></label>
                        <label className="block"><span className="text-xs font-bold uppercase tracking-widest text-slate-500 mb-1 block">Çıkış · <b>{nights} gece</b></span><input data-testid="mb-checkout" type="date" min={form.check_in} value={form.check_out} onChange={(e) => upd("check_out", e.target.value)} className={INPUT} /></label>
                        <label className="block col-span-2"><span className="text-xs font-bold uppercase tracking-widest text-slate-500 mb-1 block">Oda Tipi</span>
                            <select data-testid="mb-room" value={form.room_type_id} onChange={(e) => upd("room_type_id", e.target.value)} className={INPUT}>
                                {(hotel.room_types || []).map((rt) => <option key={rt.id} value={rt.id}>{rt.name} · {rt.capacity || rt.adult_capacity} kişi · {rt.size_sqm || "?"}m²</option>)}
                            </select>
                        </label>
                        <label className="block"><span className="text-xs font-bold uppercase tracking-widest text-slate-500 mb-1 block">Oda Sayısı</span><input data-testid="mb-rooms" type="number" min="1" value={form.rooms} onChange={(e) => upd("rooms", Number(e.target.value))} className={INPUT} /></label>
                        <label className="block"><span className="text-xs font-bold uppercase tracking-widest text-slate-500 mb-1 block">Yetişkin</span><input type="number" min="1" value={form.adults} onChange={(e) => upd("adults", Number(e.target.value))} className={INPUT} /></label>
                        <label className="block"><span className="text-xs font-bold uppercase tracking-widest text-slate-500 mb-1 block">Çocuk</span><input type="number" min="0" value={form.children} onChange={(e) => upd("children", Number(e.target.value))} className={INPUT} /></label>
                    </div>
                </section>

                {/* Guest */}
                <section className="p-5 rounded-2xl bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800">
                    <div className="flex items-center gap-2 mb-3"><User className="h-5 w-5 text-sky-500" /><h2 className="font-bold text-slate-900 dark:text-white">Misafir Bilgileri</h2></div>
                    <div className="grid grid-cols-2 gap-3">
                        <label className="block"><span className="text-xs font-bold uppercase tracking-widest text-slate-500 mb-1 block">Ad *</span><input data-testid="mb-fn" value={form.guest_first_name} onChange={(e) => upd("guest_first_name", e.target.value)} className={INPUT} /></label>
                        <label className="block"><span className="text-xs font-bold uppercase tracking-widest text-slate-500 mb-1 block">Soyad *</span><input data-testid="mb-ln" value={form.guest_last_name} onChange={(e) => upd("guest_last_name", e.target.value)} className={INPUT} /></label>
                        <label className="block"><span className="text-xs font-bold uppercase tracking-widest text-slate-500 mb-1 block"><Phone className="h-3 w-3 inline" /> Telefon *</span><input data-testid="mb-phone" value={form.guest_phone} onChange={(e) => upd("guest_phone", e.target.value)} className={INPUT} placeholder="+90 555 ..." /></label>
                        <label className="block"><span className="text-xs font-bold uppercase tracking-widest text-slate-500 mb-1 block">E-posta</span><input data-testid="mb-email" type="email" value={form.guest_email} onChange={(e) => upd("guest_email", e.target.value)} className={INPUT} /></label>
                        <label className="block col-span-2"><span className="text-xs font-bold uppercase tracking-widest text-slate-500 mb-1 block">Özel İstek / Not</span><textarea rows="2" value={form.special_requests} onChange={(e) => upd("special_requests", e.target.value)} className={INPUT} /></label>
                    </div>
                </section>

                {/* Payment */}
                <section className="p-5 rounded-2xl bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800">
                    <h2 className="font-bold text-slate-900 dark:text-white mb-3">Ödeme & Durum</h2>
                    <div className="grid grid-cols-2 gap-3">
                        <label className="block col-span-2"><span className="text-xs font-bold uppercase tracking-widest text-slate-500 mb-1 block">Toplam Tutar (₺) *</span><input data-testid="mb-total" type="number" min="0" step="100" value={form.total_amount} onChange={(e) => upd("total_amount", Number(e.target.value))} className={`${INPUT} text-lg font-bold`} /></label>
                        <label className="block">
                            <span className="text-xs font-bold uppercase tracking-widest text-slate-500 mb-1 block">Rezervasyon Durumu</span>
                            <select value={form.status} onChange={(e) => upd("status", e.target.value)} className={INPUT}>
                                <option value="confirmed">Onaylı</option>
                                <option value="pending">Beklemede</option>
                            </select>
                        </label>
                        <label className="block">
                            <span className="text-xs font-bold uppercase tracking-widest text-slate-500 mb-1 block">Ödeme Durumu</span>
                            <select value={form.payment_status} onChange={(e) => upd("payment_status", e.target.value)} className={INPUT}>
                                <option value="paid">Ödendi</option>
                                <option value="partial">Kısmi</option>
                                <option value="unpaid">Ödenmedi</option>
                            </select>
                        </label>
                        <label className="block col-span-2">
                            <span className="text-xs font-bold uppercase tracking-widest text-slate-500 mb-1 block">Kaynak</span>
                            <select value={form.source} onChange={(e) => upd("source", e.target.value)} className={INPUT}>
                                <option value="walk_in">Walk-in (kapıdan gelen)</option>
                                <option value="phone">Telefon</option>
                                <option value="whatsapp">WhatsApp</option>
                                <option value="email">E-posta</option>
                                <option value="other">Diğer</option>
                            </select>
                        </label>
                    </div>
                </section>

                <div className="p-4 rounded-xl bg-emerald-50 dark:bg-emerald-950/20 border border-emerald-200 dark:border-emerald-900 text-sm text-emerald-800 dark:text-emerald-300">
                    💡 Hızlı rezervasyon stoğunuzdan düşülür, müşteriye e-posta gönderilmez. Komisyonsuz olarak otelinize doğrudan kaydedilir.
                </div>
            </div>
        </HotelLayout>
    );
}
